import { Accessor, For, JSX, children, createSignal } from 'solid-js';
import { createListTransition } from '@solid-primitives/transition-group';
import { spring } from 'motion';
import { animate } from 'motion/mini';
import { Portal } from 'solid-js/web';

import { CountdownProgressBar1 } from './CountdownProgressBar1';

// Toast - 新版通知设计，基于 Figma Design System
// 支持标题、描述、链接，支持 4 种基础类型
// 其他图标类型通过 info + icon 参数实现

type ToastType = 'success' | 'warning' | 'error' | 'info';

interface ToastOptions {
  title: string | Accessor<JSX.Element>;
  description?: string | Accessor<JSX.Element>;
  link?: {
    text: string;
    onClick: () => void;
  };
  duration: number;
  onClick?(): void;
  system: boolean;
  icon?: Accessor<JSX.Element>;
  type: ToastType;
}

type ToastFn = (title: string | Accessor<JSX.Element>, options?: Partial<Omit<ToastOptions, 'title' | 'type'>>) => void;

interface Toast {
  (title: string | Error | Accessor<JSX.Element>, options?: Partial<Omit<ToastOptions, 'title'>>): void;
  success: ToastFn;
  warning: ToastFn;
  error: ToastFn;
  info: ToastFn;
}

// TODO: 这个需要fixed，暂时ignore
// eslint-disable-next-line
const [list, setList] = createSignal<ToastOptions[]>([]);

function push(option: ToastOptions) {
  setList((pre) => {
    // 同类型只留一个
    const filteredList = pre.filter((it) => it.type !== option.type);
    return [option, ...filteredList];
  });
}

function pop(option?: ToastOptions) {
  setList((pre) => {
    const clone = [...pre];
    const item = option || clone[clone.length - 1];
    return clone.filter((it) => it !== item);
  });
}

const animationConfig = {
  duration: 0.32,
  bounce: 0,
  type: spring,
};

export default function ToastProvider() {
  const renderList = children(() => (
    <For each={list()}>
      {(it) => {
        return <ToastItem {...it} />;
      }}
    </For>
  ));

  const aneList = createListTransition(renderList.toArray as Accessor<HTMLDivElement[]>, {
    exitMethod: 'keep-index',
    appear: true,
    onChange({ added, removed, finishRemoved }) {
      queueMicrotask(() => {
        added.forEach((el) => {
          animate(
            el,
            {
              height: [0, el.clientHeight],
            },
            animationConfig
          );
        });
      });

      removed.forEach((el) => {
        animate(
          el,
          {
            height: 0,
          },
          animationConfig
        ).then(() => finishRemoved([el]));
      });
    },
  });

  return <Portal ref={(el) => (el.className = 'toast-layer')}>{aneList()}</Portal>;
}

function ToastItem(props: ToastOptions) {
  const [hovered, setHovered] = createSignal(false);

  const defaultIcon = () => {
    switch (props.type) {
      case 'success':
        return <IconCheckSuccess class="size-7" fill="#00dc94" />;
      case 'warning':
        return <IconCheckWarning class="size-7 text-function-alert" fill="#FF8D25" />;
      case 'error':
        return <IconCheckWarning class="size-7 text-function-alert" fill="#FF8D25" />;
      case 'info':
        return <IconCheckSuccess class="size-7 text-neutral-main1_soft" fill="#FFF" />;
      default:
        return <IconCheckSuccess class="size-7" fill="#00dc94" />;
    }
  };

  const iconNode = (): JSX.Element => {
    if (props.icon) return resolveElement(props.icon);
    return defaultIcon();
  };

  return (
    <div class="overflow-hidden flex">
      <div
        class="bg-layer5_5-card flex flex-col rounded-md shadow-[0px_2px_16px_0px_rgba(10,15,19,0.05)] relative transition-all"
        onPointerEnter={() => setHovered(true)}
        onPointerLeave={() => setHovered(false)}
      >
        <div class="flex items-center">
          {/* Icon section */}
          <div class="bg-layer2 w-16 h-full flex items-center justify-center px-[18px]  shrink-0">{iconNode()}</div>

          {/* Content section */}
          <div
            class="flex flex-col gap-1 items-start justify-center max-w-[406px] pl-3 pr-2 py-4 flex-1 min-w-0"
            onClick={() => props.onClick?.()}
          >
            <div class="text-neutral-main1 text-base font-semibold leading-[1.3] overflow-hidden text-ellipsis line-clamp-2 w-full">
              {resolveElement(props.title)}
            </div>
            <Show when={props.description}>
              <div class="text-neutral-main2 text-sm font-semibold leading-[1.5] w-full line-clamp-2">
                {resolveElement(props.description!)}
              </div>
            </Show>
            <Show when={props.link}>
              <button
                class="text-left text-neutral-main2 text-sm font-semibold leading-[1.2] underline cursor-pointer hover:text-neutral-main1_alt "
                onClick={(e) => {
                  e.stopPropagation();
                  props.link!.onClick();
                }}
              >
                {props.link!.text}
              </button>
            </Show>
          </div>

          {/* Close button - always visible on the right */}
          <div class="group w-16 flex items-center justify-center px-5 shrink-0">
            <div class="size-7 flex center cursor-pointer  " onClick={() => pop(props)}>
              <IconCloseMark class="size-6 text-neutral-main2 group-hover:text-neutral-main1_alt" />
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <CountdownProgressBar1
          duration={props.duration}
          pause={hovered()}
          onEnd={() => pop(props)}
          class="h-1 w-full rounded-b-md"
        />
      </div>
    </div>
  );
}

const ignoredErrorCodes: number[] = [];

export const toast: Toast = ((title, options = {}) => {
  if (import.meta.env.SSR) throw new Error('SSR no support toast!');

  if (title instanceof Error) {
    if (ignoredErrorCodes.indexOf((title as any).code) >= 0) return '';
    return push({
      duration: options.duration ?? 6,
      onClick: options.onClick,
      system: options.system ?? false,
      title: title.message,
      description: options.description,
      link: options.link,
      icon: options.icon,
      type: options.type ?? 'error',
    });
  }

  const { duration = 6, onClick, system = false, type = 'success', description, link, icon } = options;
  return push({ duration, onClick, system, title, type, description, link, icon });
}) as Toast;

const withType =
  (type: ToastType): ToastFn =>
  (title, options = {}) => {
    return toast(title, { ...options, type });
  };

toast.success = withType('success');
toast.warning = withType('warning');
toast.error = withType('error');
toast.info = withType('info');

function resolveElement(el: string | Accessor<JSX.Element>): JSX.Element {
  return typeof el === 'function' ? el() : el;
}

/**
 * Toast 使用示例 - 基于 Figma Design System
 *
 * Figma 链接对应的 8 种通知样式：
 *
 * 1. 成功通知（带描述）
 *    Figma: https://www.figma.com/design/TAeG6Rzo857eK54hLETuCA?node-id=17561-55051
 *    用例：充值成功
 */
// toast.success('Deposit confirmed', {
//   description: 'Successfully processed your deposit of 20.000000 USDT'
// });

/**
 * 2. 警告通知（带描述和链接）
 *    Figma: https://www.figma.com/design/TAeG6Rzo857eK54hLETuCA?node-id=17611-3397
 *    用例：需要用户查看更多信息的警告
 */
// toast.warning('Deposit confirmed', {
//   description: 'Successfully processed your deposit of 20.000000 USDT',
//   link: {
//     text: 'View More',
//     onClick: () => {
//       // 跳转到详情页或打开弹窗
//       console.log('Navigate to details');
//     }
//   }
// });

/**
 * 3. 提现成功（自定义图标）
 *    Figma: https://www.figma.com/design/TAeG6Rzo857eK54hLETuCA?node-id=17602-2494
 *    用例：提现操作成功
 */
// toast.info('提现成功', {
//   description: '[Currency] [Amount] withdrawal successful.',
//   icon: () => <IconWithdraw class="size-7" />
// });

/**
 * 4. 错误通知（长文本）
 *    Figma: https://www.figma.com/design/TAeG6Rzo857eK54hLETuCA?node-id=17602-2628
 *    用例：表单验证错误
 */
// toast.error('Please use 3–16 characters with letters, numbers, and spaces between words only.between words only.');

/**
 * 5. 简短错误通知
 *    Figma: https://www.figma.com/design/TAeG6Rzo857eK54hLETuCA?node-id=17602-3234
 *    用例：必填字段验证
 */
// toast.error('Module must not be blank');

/**
 * 6. 注单结算通知（带链接）
 *    Figma: https://www.figma.com/design/TAeG6Rzo857eK54hLETuCA?node-id=17602-2840
 *    用例：投注赢钱通知
 */
// toast.info('注单结算成功', {
//   description: 'Congrats! You won 币种规范.',
//   link: {
//     text: 'View More',
//     onClick: () => {
//       // 跳转到注单详情
//       console.log('Navigate to bet details');
//     }
//   },
//   icon: () => <IconMybets class="size-7" />
// });

/**
 * 7. 密码修改成功
 *    Figma: https://www.figma.com/design/TAeG6Rzo857eK54hLETuCA?node-id=17611-3302
 *    用例：安全设置操作成功
 */
// toast.info('Password Modification Success', {
//   icon: () => <IconLock class="size-7" />
// });

/**
 * 8. 复制成功
 *    Figma: https://www.figma.com/design/TAeG6Rzo857eK54hLETuCA?node-id=17602-2934
 *    用例：复制地址、复制链接等
 */
// toast.info('复制成功', {
//   icon: () => <IconCopy class="size-7" />
// });

/**
 * 其他常用场景示例：
 */

// 简单成功通知
// toast.success('Operation successful');

// 带自定义点击事件
// toast.success('Deposit confirmed', {
//   description: 'Successfully processed your deposit',
//   onClick: () => {
//     console.log('Toast clicked');
//   }
// });

// 自定义持续时间（默认 5 秒）
// toast.success('Quick message', {
//   duration: 3
// });

// 错误对象自动转换
// try {
//   throw new Error('Something went wrong');
// } catch (error) {
//   toast(error); // 自动显示为 error 类型
// }
